import config from 'files/api.js';

const STOREFRONT_URL = `https://${config.shopify.domain}/api/2024-10/graphql.json`;

const HEADERS = {
    'Content-Type': 'application/json',
    'X-Shopify-Storefront-Access-Token': config.shopify.storefrontAccessToken,
};

// Execute a GraphQL query/mutation against the Storefront API
const gql = async (query, variables = {}) => {
    const response = await fetch(STOREFRONT_URL, {
        method: 'POST',
        headers: HEADERS,
        body: JSON.stringify({ query, variables }),
    });
    const json = await response.json();
    if(json.errors) {
        console.error('Storefront API errors:', json.errors);
        throw new Error(json.errors[0]?.message || 'Storefront API error');
    }
    return json.data;
};

// ─── GraphQL Fragments ────────────────────────────────────────────────────────

const PRODUCT_FRAGMENT = `
    fragment ProductFields on Product {
        id
        title
        handle
        description
        descriptionHtml
        productType
        tags
        availableForSale
        metafield(namespace: "custom", key: "water_sensor_tiers") { value }
        offer: metafield(namespace: "custom", key: "limited_time_offer_percent") { value }
        images(first: 5) {
            edges { node { id url altText } }
        }
        variants(first: 10) {
            edges {
                node {
                    id
                    title
                    availableForSale
                    price { amount currencyCode }
                    compareAtPrice { amount currencyCode }
                    image { url altText }
                }
            }
        }
    }
`;

const CART_FRAGMENT = `
    fragment CartFields on Cart {
        id
        checkoutUrl
        totalQuantity
        cost {
            subtotalAmount { amount currencyCode }
            totalAmount { amount currencyCode }
        }
        lines(first: 50) {
            edges {
                node {
                    id
                    quantity
                    merchandise {
                        ... on ProductVariant {
                            id
                            title
                            product { title handle }
                            image { url altText }
                            price { amount currencyCode }
                        }
                    }
                }
            }
        }
    }
`;

// ─── Format helpers ───────────────────────────────────────────────────────────

const safeJson = (s) => { try { return JSON.parse(s); } catch(_e) { return null; } };

const formatProduct = (node) => ({
    id: node.id,
    title: node.title,
    handle: node.handle,
    description: node.description,
    descriptionHtml: node.descriptionHtml,
    productType: node.productType,
    tags: node.tags,
    available: node.availableForSale,
    images: node.images.edges.map(({ node: img }) => ({
        id: img.id,
        src: img.url,
        altText: img.altText,
    })),
    variants: node.variants.edges.map(({ node: v }) => ({
        id: v.id,
        title: v.title,
        available: v.availableForSale,
        price: v.price?.amount || '0.00',
        currencyCode: v.price?.currencyCode || 'USD',
        compareAtPrice: v.compareAtPrice?.amount || null,
        image: v.image ? { src: v.image.url, altText: v.image.altText } : null,
    })),
    price: node.variants.edges[0]?.node?.price?.amount || '0.00',
    compareAtPrice: node.variants.edges[0]?.node?.compareAtPrice?.amount || null,
    // Quantity-break tiers from the product metafield (single source of truth).
    tiers: safeJson(node.metafield?.value),
    // Limited-time offer percent from the product metafield (the Shopify Function
    // reads the same one). Only applies while an offer is live — see LimitedOffer.js.
    offerPercent: parseFloat(node.offer?.value) || 0,
});

const formatCart = (cart) => ({
    id: cart.id,
    checkoutUrl: cart.checkoutUrl,
    webUrl: cart.checkoutUrl,
    totalQuantity: cart.totalQuantity,
    subtotalPrice: {
        amount: cart.cost?.subtotalAmount?.amount || '0.00',
        currencyCode: cart.cost?.subtotalAmount?.currencyCode || 'USD',
    },
    totalPrice: {
        amount: cart.cost?.totalAmount?.amount || '0.00',
        currencyCode: cart.cost?.totalAmount?.currencyCode || 'USD',
    },
    lineItems: cart.lines.edges.map(({ node: line }) => ({
        id: line.id,
        quantity: line.quantity,
        title: line.merchandise?.product?.title || '',
        handle: line.merchandise?.product?.handle || '',
        variant: {
            id: line.merchandise?.id,
            title: line.merchandise?.title,
            price: {
                amount: line.merchandise?.price?.amount || '0.00',
                currencyCode: line.merchandise?.price?.currencyCode || 'USD',
            },
            image: line.merchandise?.image
                ? { src: line.merchandise.image.url, altText: line.merchandise.image.altText }
                : null,
        },
    })),
});

// ─── Public API ───────────────────────────────────────────────────────────────

const ShopifyClient = {

    // Fetch all products (up to 50)
    fetchAllProducts: async () => {
        try {
            const data = await gql(`
                ${PRODUCT_FRAGMENT}
                query { products(first: 50) { edges { node { ...ProductFields } } } }
            `);
            return data.products.edges.map(({ node }) => formatProduct(node));
        } catch(e) {
            console.error('Error fetching products:', e);
            return [];
        }
    },

    // Fetch a single product by handle
    fetchProduct: async (handle) => {
        try {
            const data = await gql(`
                ${PRODUCT_FRAGMENT}
                query GetProduct($handle: String!) {
                    productByHandle(handle: $handle) { ...ProductFields }
                }
            `, { handle });
            return data.productByHandle ? formatProduct(data.productByHandle) : null;
        } catch(e) {
            console.error('Error fetching product:', e);
            return null;
        }
    },

    // Create a new cart (replaces deprecated checkoutCreate)
    createCheckout: async () => {
        try {
            const data = await gql(`
                ${CART_FRAGMENT}
                mutation { cartCreate { cart { ...CartFields } userErrors { field message } } }
            `);
            if(data.cartCreate.userErrors?.length > 0) {
                throw new Error(data.cartCreate.userErrors[0].message);
            }
            return formatCart(data.cartCreate.cart);
        } catch(e) {
            console.error('Error creating cart:', e);
            return null;
        }
    },

    // Fetch an existing cart by ID
    fetchCheckout: async (cartId) => {
        try {
            const data = await gql(`
                ${CART_FRAGMENT}
                query GetCart($cartId: ID!) { cart(id: $cartId) { ...CartFields } }
            `, { cartId });
            return data.cart ? formatCart(data.cart) : null;
        } catch(e) {
            console.error('Error fetching cart:', e);
            return null;
        }
    },

    // Add items to cart — lines: [{ variantId, quantity }]
    addToCheckout: async (cartId, lineItems) => {
        // Convert from legacy { variantId, quantity } to Cart API { merchandiseId, quantity }
        const lines = lineItems.map(item => ({
            merchandiseId: item.variantId,
            quantity: item.quantity,
        }));
        try {
            const data = await gql(`
                ${CART_FRAGMENT}
                mutation CartLinesAdd($cartId: ID!, $lines: [CartLineInput!]!) {
                    cartLinesAdd(cartId: $cartId, lines: $lines) {
                        cart { ...CartFields }
                        userErrors { field message }
                    }
                }
            `, { cartId, lines });
            if(data.cartLinesAdd.userErrors?.length > 0) {
                throw new Error(data.cartLinesAdd.userErrors[0].message);
            }
            return formatCart(data.cartLinesAdd.cart);
        } catch(e) {
            console.error('Error adding to cart:', e);
            return null;
        }
    },

    // Update line quantities — lineItems: [{ id, quantity }]
    updateCheckout: async (cartId, lineItems) => {
        try {
            const data = await gql(`
                ${CART_FRAGMENT}
                mutation CartLinesUpdate($cartId: ID!, $lines: [CartLineUpdateInput!]!) {
                    cartLinesUpdate(cartId: $cartId, lines: $lines) {
                        cart { ...CartFields }
                        userErrors { field message }
                    }
                }
            `, { cartId, lines: lineItems });
            if(data.cartLinesUpdate.userErrors?.length > 0) {
                throw new Error(data.cartLinesUpdate.userErrors[0].message);
            }
            return formatCart(data.cartLinesUpdate.cart);
        } catch(e) {
            console.error('Error updating cart:', e);
            return null;
        }
    },

    // Remove line items — lineItemIds: [id, ...]
    removeFromCheckout: async (cartId, lineItemIds) => {
        try {
            const data = await gql(`
                ${CART_FRAGMENT}
                mutation CartLinesRemove($cartId: ID!, $lineIds: [ID!]!) {
                    cartLinesRemove(cartId: $cartId, lineIds: $lineIds) {
                        cart { ...CartFields }
                        userErrors { field message }
                    }
                }
            `, { cartId, lineIds: lineItemIds });
            if(data.cartLinesRemove.userErrors?.length > 0) {
                throw new Error(data.cartLinesRemove.userErrors[0].message);
            }
            return formatCart(data.cartLinesRemove.cart);
        } catch(e) {
            console.error('Error removing from cart:', e);
            return null;
        }
    },

    // Get the checkout URL for a cart
    getCheckoutUrl: (cart) => {
        return cart ? cart.checkoutUrl || cart.webUrl : null;
    },
};

export default ShopifyClient;
